import { GaylordArrivalScanType, SortationCenterError, SortationGaylordError } from "@deliverr/sortation-client";
import {
  createNotificationCreator,
  createSuccessNotification,
  log,
  logError,
  logStart,
  SoundFx,
} from "@deliverr/ui-facility";
import { useCommonFlow } from "@deliverr/ui-facility/lib-facility/flow/useCommonFlow";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useAsyncFn } from "react-use";
import { useRecoilValue } from "recoil";

import { sortationIdState } from "sortation/base/sortationIdState";
import { useSortationCenterError } from "sortation/base/useSortationCenterError";
import { useSortationClient } from "sortation/base/useSortationClient";
import { useSortationModal } from "sortation/components/SortationModal";
import { visibleSortationModalValue } from "sortation/components/SortationModal/SortationModalState";
import { TOAST_AUTO_CLOSE } from "sortation/utils/toastAutoClose";
import { ZoneSkipArrivalScanModal, zoneSkipArrivalScanMessageMap } from "./modals";

const createWarningNotification = createNotificationCreator("WARNING");

export const useZoneSkipArrivalScan = () => {
  const { formatMessage } = useIntl();
  const { successResponse, customResponse, resetNotifications, addAutoCloseNotification, emitFlash } = useCommonFlow();
  const { handleSortationCenterError } = useSortationCenterError();
  const sortationId = useRecoilValue(sortationIdState);
  const sortationClient = useSortationClient();
  const { showModal } = useSortationModal();
  const visibleModal = useRecoilValue(visibleSortationModalValue);

  const [barcode, setBarcode] = useState<string>("");

  const warningResponse = useMemo(() => customResponse(SoundFx.INFO, "WARNING" as any), [customResponse]);

  const handleError = useCallback(
    (err) => {
      const errCode = err.message;
      let modal: ZoneSkipArrivalScanModal;

      switch (errCode) {
        case SortationGaylordError.NOT_FOUND:
          modal = ZoneSkipArrivalScanModal.GAYLORD_NOT_FOUND;
          break;
        case SortationGaylordError.GAYLORD_NOT_ELIGIBLE_FOR_TRANSFER:
          modal = ZoneSkipArrivalScanModal.GAYLORD_NOT_ELIGIBLE;
          break;
        case SortationGaylordError.GAYLORD_NOT_ELIGIBLE_FOR_ARRIVAL_SCAN:
          modal = ZoneSkipArrivalScanModal.GAYLORD_NOT_ELIGIBLE_FOR_ARRIVAL_SCAN;
          break;
        default:
          modal = ZoneSkipArrivalScanModal.UNKNOWN_ERROR;
          break;
      }

      emitFlash("DANGER");
      showModal(modal, zoneSkipArrivalScanMessageMap[modal]);
    },
    [emitFlash, showModal]
  );

  const [handleZoneSkipArrivalScanState, handleZoneSkipArrivalScan] = useAsyncFn(
    async (input: string) => {
      if (visibleModal) {
        return;
      }

      const ctx = logStart({ fn: "useZoneSkipArrivalScan.handleZoneSkipArrivalScan", input, sortationId });

      setBarcode(input);
      resetNotifications();

      try {
        const response = await sortationClient.processGaylordArrivalScan(input, sortationId!);

        if (response.scanType === GaylordArrivalScanType.GAYLORD_TRANSFER_SCAN) {
          if (response.preSortMetadata?.isTransferAlreadyCompleted) {
            successResponse();
            addAutoCloseNotification(
              createSuccessNotification(
                formatMessage({
                  id: "sortation.zoneSkipArrivalScan.alreadyCompleted",
                  defaultMessage: "Transfer completed. Take directly to dock door.",
                })
              ),
              TOAST_AUTO_CLOSE
            );
            log(ctx, "Transfer already completed", { eventId: response.preSortMetadata.eventId });
            return;
          }

          successResponse();
          addAutoCloseNotification(
            createSuccessNotification(
              formatMessage(
                {
                  id: "sortation.zoneSkipArrivalScan.presorted",
                  defaultMessage: "Gaylord is presorted. Transfer started: {origin} to {destination}.",
                },
                {
                  origin: response.originGaylordId,
                  destination: response.preSortMetadata?.destinationGaylordId ?? "",
                }
              )
            ),
            TOAST_AUTO_CLOSE
          );

          log(ctx, "Presorted gaylord transfer initiated", { eventId: response.preSortMetadata?.eventId });
          return;
        }

        warningResponse();
        addAutoCloseNotification(
          createWarningNotification(
            formatMessage(
              {
                id: "sortation.zoneSkipArrivalScan.arrivalSuccess",
                defaultMessage: "Gaylord arrival scan complete for {origin}. Ready for resorting.",
              },
              {
                origin: response.originGaylordId,
              }
            )
          ),
          TOAST_AUTO_CLOSE
        );

        log(ctx, "Gaylord arrival scan completed", { originGaylordId: response.originGaylordId });
      } catch (err) {
        logError(ctx, err);
        if (err.message === SortationCenterError.SORTATION_CENTER_NOT_FOUND) {
          handleSortationCenterError();
          return;
        }
        handleError(err);
      } finally {
        setBarcode("");
      }
    },
    [
      sortationId,
      visibleModal,
      formatMessage,
      resetNotifications,
      sortationClient,
      successResponse,
      warningResponse,
      addAutoCloseNotification,
      handleSortationCenterError,
      handleError,
    ]
  );

  return {
    barcode,
    isLoading: handleZoneSkipArrivalScanState.loading,
    setBarcode,
    handleZoneSkipArrivalScan,
  };
};
